import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage } from "../lib/api";
import { hasPermission } from "../lib/permissions";
import {
  ACCOUNT_STATE_LABELS, allowedTransitions, buildDatesCorrectionPayload, buildStatusPayload, canCorrectDates, isDatesCorrectionValid, buildUpdatePayload, describeHistoryEntry, errorInfo, formatDate, formatPhone, hasChanges,
  isDuplicateRefusal, isFieldLocked, isOverrideValid, isStatusFormValid, periodsFromHistory, primaryTransition, statusExplanation, statusFormSpec,
  telHref, whatsappHref,
} from "../lib/collaborators";
import useCollabReferentials from "../hooks/useCollabReferentials";
import useSkillsReferentials from "../hooks/useSkillsReferentials";
import CollaboratorSkillsCard from "../components/CollaboratorSkillsCard";
import CollaboratorMandates from "../components/CollaboratorMandates";
import useMandates from "../hooks/useMandates";
import { exitNotice, mandateBadges } from "../lib/mandates";
import CollaboratorTeamCard from "../components/CollaboratorTeamCard";
import RelatedSessions from "../components/training/RelatedSessions";
import { CATALOG_READ_KEYS } from "../lib/training";
import CollaboratorAccreditations from "../components/CollaboratorAccreditations";
import JobFunctionsField from "../components/JobFunctionsField";
import SuggestedRoleHint from "../components/SuggestedRoleHint";
import { suggestedRoles } from "../lib/skills";
import CollaboratorPicker from "../components/CollaboratorPicker";
import HandoverPanel, { useHandoverImpact } from "../components/HandoverPanel";
import { blockedReason, buildHandover, emptyChoices, impactFromError, isHandoverConflict, pendingCount, pruneChoices } from "../lib/handover";
import DuplicateNotice from "../components/DuplicateNotice";
import { Initials, StatusBadge } from "../components/CollaboratorBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { MobileSheet, MobileSheetBody, MobileSheetClose, MobileSheetCloseButton, MobileSheetContent, MobileSheetHeader, MobileSheetTitle, MobileSheetTrigger } from "../components/ui/mobile-sheet";
import { toast } from "sonner";
import { AlertCircle, Archive, ArchiveRestore, ArrowLeft, History, Link2, Loader2, Mail, MessageCircle, MoreHorizontal, Pencil, Phone, Trash2, Unlink, UserPlus } from "lucide-react";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";

const NONE = "__none__";
const today = () => new Date().toISOString().slice(0, 10);

// A contact value with its one-tap action (call, e-mail, WhatsApp): the main gesture of a directory, on mobile above all.
function ContactValue({ value, href, icon: Icon, label, external }) {
  if (!value) return <span className="text-slate-400">Non renseigné</span>;
  return (
    <a href={href} className="inline-flex items-center gap-1.5 text-jamora hover:underline break-all" aria-label={`${label} ${value}`}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
      <Icon size={14} className="shrink-0" aria-hidden="true" /> {value}
    </a>
  );
}

function Row({ label, children, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-0.5 sm:gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
      <dt className="text-xs sm:text-sm text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900 break-words">{children || <span className="text-slate-400">Non renseigné</span>}</dd>
    </div>
  );
}

export default function CollaboratorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const refs = useCollabReferentials();
  const skillRefs = useSkillsReferentials();
  const isAdmin = user?.role === "admin";
  const canEdit = hasPermission(user, "collaborators:edit");
  const canArchive = hasPermission(user, "collaborators:archive");
  const canDelete = hasPermission(user, "collaborators:delete") && hasPermission(user, "collaborators:edit");
  const canAssess = hasPermission(user, "skills:assess");
  const canGrant = hasPermission(user, "accreditations:grant");

  const [fiche, setFiche] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound | error
  const [entries, setEntries] = useState([]);
  const [historyMore, setHistoryMore] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState("");
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const openStatus = (to = null) => { setStatusTarget(to); setStatusOpen(true); };
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);

  const details = !!fiche && fiche.contacts !== undefined;
  const mandates = useMandates(id, details);

  const loadHistory = useCallback(async () => {
    const all = [];
    let cursor = null;
    try {
      for (let guard = 0; guard < 20; guard += 1) {
        const { data } = await api.get(`/collaborators/${id}/history`, { params: { limit: 100, ...(cursor ? { cursor } : {}) } });
        all.push(...data.items);
        cursor = data.next_cursor;
        if (!data.has_more) break;
      }
      setEntries(all);
      setHistoryMore(false);
    } catch (e) {
      setEntries(all);
      setHistoryMore(true);
    }
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/collaborators/${id}`);
      setFiche(data);
      setState("ready");
      if (data.contacts !== undefined) loadHistory();
    } catch (e) {
      setState(e.response?.status === 404 ? "notfound" : "error");
    }
  }, [id, loadHistory]);

  useEffect(() => {
    setState("loading");
    setEditing(false);
    load();
  }, [load]);

  const startEdit = () => {
    setDraft({
      first_name: fiche.first_name || "", last_name: fiche.last_name || "", relation_type: fiche.relation_type, job_title: fiche.job_title || "", job_function_ids: fiche.job_function_ids || [],
      service_id: fiche.service_id || "", manager_id: fiche.manager_id || "", manager_user_id: fiche.manager_user_id || "", manager_label: fiche.manager?.display_name || "",
      top_level: !!fiche.top_level, zone_ids: fiche.zone_ids || [], contacts: { email: fiche.contacts?.email || "", phone: fiche.contacts?.phone || "", whatsapp: fiche.contacts?.whatsapp || "" },
    });
    setRefusal(null);
    setConfirmed(false);
    setReason("");
    setEditing(true);
  };

  const payload = useMemo(() => (editing && draft ? buildUpdatePayload(fiche, draft, fiche.version) : null), [editing, draft, fiche]);
  const dirty = !!payload && hasChanges(payload);

  const change = (patch) => {
    setRefusal(null);
    setConfirmed(false);
    setDraft((d) => ({ ...d, ...patch }));
  };

  const handleFailure = (error) => {
    const info = errorInfo(error);
    if (info.code === "version_conflict") {
      toast.error(info.data.msg);
      setEditing(false);
      load();
      return true;
    }
    return false;
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const body = { ...payload };
      if (refusal?.can_override && confirmed) {
        body.confirm_duplicates = true;
        body.override_reason = reason.trim();
      }
      await api.patch(`/collaborators/${id}`, body);
      setEditing(false);
      toast.success("Modifications enregistrées");
      await load();
    } catch (error) {
      const info = errorInfo(error);
      if (isDuplicateRefusal(info)) setRefusal(info.data);
      else if (!handleFailure(error)) toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="space-y-4" data-testid="collab-detail-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (state === "notfound" || state === "error") {
    return (
      <Card className="p-10 text-center space-y-3 max-w-xl mx-auto" data-testid={state === "notfound" ? "collab-notfound" : "collab-detail-error"}>
        <AlertCircle className="mx-auto text-slate-300" size={32} />
        <p className="font-medium text-slate-900">{state === "notfound" ? "Fiche introuvable" : "Impossible de charger la fiche"}</p>
        <p className="text-sm text-slate-500">
          {state === "notfound" ? "Cette fiche n'existe pas ou n'est pas dans votre périmètre." : "Réessayez dans quelques instants."}
        </p>
        <div className="flex justify-center gap-2">
          {state === "error" && <Button variant="outline" onClick={load}>Réessayer</Button>}
          <Button asChild variant="outline"><Link to="/collaborateurs">Retour aux collaborateurs</Link></Button>
        </div>
      </Card>
    );
  }

  const editable = canEdit && !fiche.archived;
  const periods = periodsFromHistory(entries, fiche);
  const lock = (field) => isFieldLocked(field, fiche, user);

  return (
    <div className="space-y-4" data-testid="collab-detail-page">
      <Link to="/collaborateurs" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Retour aux collaborateurs
      </Link>

      <div className="flex items-start justify-between gap-3 md:flex-wrap">
        <div className="flex flex-1 md:flex-none items-start gap-3 min-w-0">
          <Initials name={fiche.display_name} seed={fiche.id} className="w-11 h-11 text-sm" />
          <div className="min-w-0">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="collab-name">{fiche.display_name}</h1>
            {fiche.job_title && <div className="text-sm text-slate-600" data-testid="collab-job-title">{fiche.job_title}</div>}
            <div className="flex items-center flex-wrap gap-2 mt-1">
              <span className="text-xs text-slate-500" data-testid="collab-reference">{fiche.reference}</span>
              <StatusBadge status={fiche.status} archived={fiche.archived} />
              {fiche.is_me && <Badge variant="outline" className="text-2xs">Ma fiche</Badge>}
              {mandateBadges(mandates.items).map((b) => (
                <Badge key={b.key} variant="outline" className={`text-2xs ${b.verified ? "border-jamora text-jamora" : "border-dashed border-amber-400 text-amber-800"}`} data-testid="collab-mandate-badge">{b.label}</Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2">
          {editable && !editing && details && (
            <Button onClick={startEdit} data-testid="btn-collab-edit">
              <Pencil size={14} className="mr-1.5" /> Modifier
            </Button>
          )}
          {(canEdit || canArchive || canDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="btn-collab-actions"><MoreHorizontal size={16} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem onClick={() => openStatus()} disabled={fiche.archived || (fiche.is_me && !isAdmin)} data-testid="action-collab-status">
                    Changer le statut
                  </DropdownMenuItem>
                )}
                {canEdit && (
                  <DropdownMenuItem onClick={() => setReassignOpen(true)} disabled={fiche.archived} data-testid="action-collab-reassign">Réaffecter l'équipe</DropdownMenuItem>
                )}
                {canCorrectDates(fiche, user) && (
                  <DropdownMenuItem onClick={() => setDatesOpen(true)} data-testid="action-collab-dates">Corriger les dates</DropdownMenuItem>
                )}
                {canEdit && canArchive && <DropdownMenuSeparator />}
                {canArchive && (
                  <DropdownMenuItem onClick={() => setArchiveOpen(true)} disabled={fiche.is_me && !isAdmin} data-testid="action-collab-archive">
                    {fiche.archived ? <><ArchiveRestore size={14} className="mr-2" /> Désarchiver</> : <><Archive size={14} className="mr-2" /> Archiver</>}
                  </DropdownMenuItem>
                )}
                {canDelete && !fiche.archived && (
                  <DropdownMenuItem className="text-red-700" onClick={() => setDeleteOpen(true)} disabled={fiche.is_me && !isAdmin} data-testid="action-collab-delete">
                    <Trash2 size={14} className="mr-2" /> Supprimer…
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {(editable && !editing && details || canEdit || canArchive || canDelete) && (
          <MobileSheet>
            <MobileSheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden shrink-0" aria-label="Actions collaborateur" data-testid="btn-collab-detail-actions-mobile">
                <MoreHorizontal size={20} />
              </Button>
            </MobileSheetTrigger>
            <MobileSheetContent className="md:hidden" aria-describedby={undefined}>
              <MobileSheetHeader>
                <MobileSheetTitle>Actions collaborateur</MobileSheetTitle>
                <MobileSheetCloseButton />
              </MobileSheetHeader>
              <MobileSheetBody>
                <div className="space-y-2">
                  {editable && !editing && details && <MobileSheetClose asChild>
                    <Button className="w-full justify-start gap-3" onClick={startEdit} data-testid="btn-collab-edit-mobile">
                      <Pencil size={18} /> Modifier
                    </Button>
                  </MobileSheetClose>}
                  {canEdit && <MobileSheetClose asChild>
                    <Button variant="outline" className="w-full justify-start" onClick={() => openStatus()} disabled={fiche.archived || (fiche.is_me && !isAdmin)} data-testid="action-collab-status-mobile">
                      Changer le statut
                    </Button>
                  </MobileSheetClose>}
                  {canEdit && <MobileSheetClose asChild>
                    <Button variant="outline" className="w-full justify-start" onClick={() => setReassignOpen(true)} disabled={fiche.archived} data-testid="action-collab-reassign-mobile">
                      Réaffecter l'équipe
                    </Button>
                  </MobileSheetClose>}
                  {canCorrectDates(fiche, user) && <MobileSheetClose asChild>
                    <Button variant="outline" className="w-full justify-start" onClick={() => setDatesOpen(true)} data-testid="action-collab-dates-mobile">
                      Corriger les dates
                    </Button>
                  </MobileSheetClose>}
                  {canArchive && <MobileSheetClose asChild>
                    <Button variant="outline" className="w-full justify-start gap-3" onClick={() => setArchiveOpen(true)} disabled={fiche.is_me && !isAdmin} data-testid="action-collab-archive-mobile">
                      {fiche.archived ? <><ArchiveRestore size={18} /> Désarchiver</> : <><Archive size={18} /> Archiver</>}
                    </Button>
                  </MobileSheetClose>}
                  {canDelete && !fiche.archived && <MobileSheetClose asChild>
                    <Button variant="outline" className="w-full justify-start gap-3 text-red-700" onClick={() => setDeleteOpen(true)} disabled={fiche.is_me && !isAdmin} data-testid="action-collab-delete-mobile">
                      <Trash2 size={18} /> Supprimer…
                    </Button>
                  </MobileSheetClose>}
                </div>
              </MobileSheetBody>
            </MobileSheetContent>
          </MobileSheet>
        )}
      </div>

      {!fiche.archived && statusExplanation(fiche) && (
        <div className={`rounded-lg border px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-3 ${fiche.status === "suspended" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-700"}`}
          data-testid="collab-status-banner">
          <div className="flex items-center gap-2 flex-1">
            <StatusBadge status={fiche.status} archived={false} />
            <span>{statusExplanation(fiche)}</span>
          </div>
          {canEdit && (!fiche.is_me || isAdmin) && primaryTransition(fiche.status) && (
            <Button size="sm" onClick={() => openStatus(primaryTransition(fiche.status).to)} className="self-start sm:self-auto"
              data-testid="btn-collab-primary-status">
              {primaryTransition(fiche.status).label}
            </Button>
          )}
        </div>
      )}
      {fiche.archived && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="collab-archived-banner">
          Cette fiche est archivée : elle est masquée des listes et ne peut plus être modifiée. Son statut ({fiche.status === "exited" ? "sorti" : "préinscrit"}) est conservé.
        </div>
      )}
      {isAdmin && fiche.account?.state === "disabled" && fiche.status !== "exited" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="warn-account-disabled">
          Le compte CRM rattaché à cette personne est désactivé alors qu'elle est toujours {fiche.status === "active" ? "active" : "dans l'annuaire"}.
        </div>
      )}
      {isAdmin && fiche.account?.state === "active" && fiche.status === "exited" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="warn-account-active">
          Cette personne est sortie mais son compte CRM est toujours actif. Désactivez-le depuis la page Utilisateurs si nécessaire.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 items-start">
        <div className="space-y-4">
          <Card className="p-5 space-y-2">
            <div className="font-display font-semibold text-sm text-slate-900 mb-1">Identité et coordonnées</div>
            {!details && <p className="text-sm text-slate-500" data-testid="collab-minimal-note">Les coordonnées ne sont visibles qu'avec l'autorisation adéquate.</p>}
            {details && !editing && (
              <dl>
                <Row label="Prénom">{fiche.first_name}</Row>
                <Row label="Nom">{fiche.last_name}</Row>
                <Row label="Email" testid="row-email"><ContactValue value={fiche.contacts.email} href={`mailto:${fiche.contacts.email}`} icon={Mail} label="Écrire à" /></Row>
                <Row label="Téléphone" testid="row-phone"><ContactValue value={formatPhone(fiche.contacts.phone)} href={telHref(fiche.contacts.phone)} icon={Phone} label="Appeler le" /></Row>
                <Row label="WhatsApp" testid="row-whatsapp"><ContactValue value={formatPhone(fiche.contacts.whatsapp)} href={whatsappHref(fiche.contacts.whatsapp)} icon={MessageCircle} label="Écrire sur WhatsApp au" external /></Row>
              </dl>
            )}
            {details && editing && (
              <div className="space-y-4" data-testid="collab-edit-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label htmlFor="e-first">Prénom</Label>
                    <Input id="e-first" className="text-base md:text-sm" data-testid="edit-first-name" value={draft.first_name} onChange={(e) => change({ first_name: e.target.value })} /></div>
                  <div className="space-y-2"><Label htmlFor="e-last">Nom</Label>
                    <Input id="e-last" className="text-base md:text-sm" data-testid="edit-last-name" value={draft.last_name} onChange={(e) => change({ last_name: e.target.value })} /></div>
                  <div className="space-y-2 md:col-span-2"><Label htmlFor="e-email">Email</Label>
                    <Input id="e-email" type="email" inputMode="email" className="text-base md:text-sm" data-testid="edit-email" value={draft.contacts.email}
                      onChange={(e) => change({ contacts: { ...draft.contacts, email: e.target.value } })} /></div>
                  <div className="space-y-2"><Label htmlFor="e-phone">Téléphone</Label>
                    <Input id="e-phone" type="tel" inputMode="tel" className="text-base md:text-sm" data-testid="edit-phone" value={draft.contacts.phone}
                      onChange={(e) => change({ contacts: { ...draft.contacts, phone: e.target.value } })} /></div>
                  <div className="space-y-2"><Label htmlFor="e-wa">WhatsApp</Label>
                    <Input id="e-wa" type="tel" inputMode="tel" className="text-base md:text-sm" data-testid="edit-whatsapp" value={draft.contacts.whatsapp}
                      onChange={(e) => change({ contacts: { ...draft.contacts, whatsapp: e.target.value } })} /></div>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-5 space-y-2">
            <div className="font-display font-semibold text-sm text-slate-900 mb-1">Organisation</div>
            {!editing && (
              <dl>
                <Row label="Fonction" testid="row-job-title">{fiche.job_title}</Row>
                <Row label="Type de relation" testid="row-type">{refs.relationLabel(fiche.relation_type)}</Row>
                <Row label="Service" testid="row-service">{refs.serviceName(fiche.service_id)}</Row>
                {details && (
                  <Row label="Responsable" testid="row-manager">
                    {fiche.manager?.hidden ? <span className="text-slate-500">Hors de votre périmètre</span> : fiche.manager?.kind === "account" ? (
                      <span>{fiche.manager.display_name} <span className="text-xs text-slate-500">· compte CRM, sans fiche{fiche.manager.inactive ? " (désactivé)" : ""}</span></span>
                    ) : fiche.manager ? (
                      <Link className="text-jamora hover:underline" to={`/collaborateurs/${fiche.manager.id}`}>{fiche.manager.display_name}</Link>
                    ) : null}
                  </Row>
                )}
                {details && fiche.top_level && <Row label="Position" testid="row-top-level">Sommet de l'organisation</Row>}
                {details && <Row label="Zones" testid="row-zones">{(fiche.zone_ids || []).map(refs.zoneLabel).join(", ")}</Row>}
              </dl>
            )}
            {editing && (
              <div className="space-y-4" data-testid="collab-edit-org">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="e-job">Fonction</Label>
                    <Input id="e-job" className="text-base md:text-sm" data-testid="edit-job-title" value={draft.job_title} maxLength={120} disabled={lock("job_title")}
                      placeholder="Ex. : Formateur POS, Commercial terrain" onChange={(e) => change({ job_title: e.target.value })} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Fonctions métier</Label>
                    <JobFunctionsField testid="edit-job-functions" functions={skillRefs.functions} value={draft.job_function_ids} disabled={lock("job_function_ids")}
                      onChange={(ids) => change({ job_function_ids: ids })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Type de relation</Label>
                    <Select value={draft.relation_type} onValueChange={(v) => change({ relation_type: v })} disabled={lock("relation_type")}>
                      <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {refs.relation_types.filter((r) => r.active || r.code === fiche.relation_type).map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Service</Label>
                    <Select value={draft.service_id || NONE} onValueChange={(v) => change({ service_id: v === NONE ? "" : v })} disabled={lock("service_id")}>
                      <SelectTrigger data-testid="edit-service"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Aucun service</SelectItem>
                        {refs.services.filter((s) => s.active || s.id === fiche.service_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.active ? "" : " (inactif)"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Responsable</Label>
                    <CollaboratorPicker testid="edit-manager" value={draft.manager_id || draft.manager_user_id} label={draft.manager_label} excludeId={fiche.id} disabled={lock("manager_id") || !!draft.top_level} allowAccounts
                      onChange={(pid, name, kind) => change({ manager_id: kind === "collaborator" ? pid || "" : "", manager_user_id: kind === "account" ? pid : "", manager_label: name || "" })} />
                  </div>
                  {isAdmin && (
                    <label className="flex items-start gap-2 text-sm md:col-span-2" data-testid="edit-top-level-label">
                      <Checkbox className="mt-0.5" checked={!!draft.top_level} data-testid="edit-top-level"
                        onCheckedChange={(v) => change(v === true ? { top_level: true, manager_id: "", manager_user_id: "", manager_label: "" } : { top_level: false })} />
                      <span>
                        Sommet de l'organisation
                        <span className="block text-xs text-slate-500">Cette personne n'a pas de responsable. Plusieurs personnes peuvent être au sommet, sans lien entre elles.</span>
                      </span>
                    </label>
                  )}
                </div>
                {refs.zones.length > 0 && (
                  <div className="space-y-2">
                    <Label>Zones</Label>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {refs.zones.filter((z) => z.active || draft.zone_ids.includes(z.code)).map((z) => (
                        <label key={z.code} className="flex items-center gap-2 text-sm min-h-[32px]">
                          <Checkbox checked={draft.zone_ids.includes(z.code)} data-testid={`edit-zone-${z.code}`}
                            onCheckedChange={(v) => change({ zone_ids: v === true ? [...draft.zone_ids, z.code] : draft.zone_ids.filter((c) => c !== z.code) })} />
                          {z.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {fiche.is_me && !isAdmin && <p className="text-xs text-slate-500">La fonction, les fonctions métier, le type de relation, le service et le responsable de votre propre fiche sont gérés par un administrateur.</p>}
              </div>
            )}
          </Card>

          {editing && (
            <>
              <DuplicateNotice refusal={refusal} confirmed={confirmed} reason={reason} onConfirmedChange={setConfirmed} onReasonChange={setReason} />
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button variant="outline" className="h-11 sm:h-10" onClick={() => setEditing(false)} disabled={saving} data-testid="btn-collab-cancel">Annuler</Button>
                <Button className="h-11 sm:h-10" onClick={save} data-testid="btn-collab-save"
                  disabled={!dirty || saving || (refusal?.can_override && confirmed && !isOverrideValid(confirmed, reason))}>
                  {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}
                  {refusal?.can_override && confirmed ? "Enregistrer malgré l'alerte" : "Enregistrer"}
                </Button>
              </div>
            </>
          )}

          {details && !editing && <CollaboratorTeamCard fiche={fiche} serviceName={refs.serviceName} />}

          {user?.features?.training && hasPermission(user, CATALOG_READ_KEYS) && (
            <RelatedSessions params={{ collaborator_id: fiche.id }} title="Formations suivies ou animées" testid="collaborator-sessions" hideWhenEmpty />
          )}

          {skillRefs.status === "ready" && (
            <>
              <CollaboratorSkillsCard fiche={fiche} refs={skillRefs} canAssess={canAssess && (!fiche.is_me || isAdmin)} isAdmin={isAdmin} onHistoryChange={details ? loadHistory : undefined} />
              <CollaboratorAccreditations fiche={fiche} refs={skillRefs} canGrant={canGrant} onHistoryChange={details ? loadHistory : undefined} />
            </>
          )}

          {details && mandates.status !== "idle" && <CollaboratorMandates fiche={fiche} mandates={mandates} isAdmin={isAdmin} features={user?.features} onHistoryChange={loadHistory} />}

          {details && (
            <Card className="p-5 space-y-2" data-testid="collab-periods">
              <div className="font-display font-semibold text-sm text-slate-900 mb-1">Parcours</div>
              {periods.length === 0 ? (
                <p className="text-sm text-slate-500">Aucune période enregistrée : la personne n'a pas encore commencé.</p>
              ) : (
                <ul className="space-y-1.5">
                  {periods.map((p, index) => (
                    <li key={`${p.entry_date}-${index}`} className="text-sm text-slate-700" data-testid="period-item">
                      Du {formatDate(p.entry_date)} {p.exit_date ? `au ${formatDate(p.exit_date)}` : p.current ? "à aujourd'hui" : ""}
                      {p.current && <span className="ml-2 text-xs text-emerald-700">en cours</span>}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>

        <div className="space-y-4">
          {isAdmin && <AccountCard fiche={fiche} reload={load} onFailure={handleFailure} suggestions={suggestedRoles(skillRefs.functions, fiche.job_function_ids)} />}

          {details && (
            <Card className="p-5 space-y-3" data-testid="collab-history">
              <div className="flex items-center gap-2 font-display font-semibold text-sm text-slate-900"><History size={15} /> Historique</div>
              {entries.length === 0 ? <p className="text-sm text-slate-500">Aucun événement.</p> : (
                <ol className="space-y-2.5">
                  {entries.map((e) => (
                    <li key={e.id} className="text-sm" data-testid="history-item">
                      <div className="text-slate-900">{describeHistoryEntry(e, skillRefs.levelLabel)}</div>
                      <div className="text-xs text-slate-500">{new Date(e.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                      {e.reason && <div className="text-xs text-slate-600 italic">« {e.reason} »</div>}
                    </li>
                  ))}
                </ol>
              )}
              {historyMore && <Button variant="outline" size="sm" onClick={loadHistory}>Réessayer de charger la suite</Button>}
            </Card>
          )}
        </div>
      </div>

      <ReassignDialog open={reassignOpen} onOpenChange={setReassignOpen} fiche={fiche} onDone={load} onFailure={handleFailure} />
      <StatusDialog open={statusOpen} onOpenChange={setStatusOpen} fiche={fiche} initialTo={statusTarget} mandateNotice={exitNotice(mandates.items)} onDone={() => { load(); mandates.reload(); }} onFailure={handleFailure} />
      <DatesDialog open={datesOpen} onOpenChange={setDatesOpen} fiche={fiche} onDone={() => { load(); loadHistory?.(); }} onFailure={handleFailure} />
      <DeleteWithReasonDialog
        target={deleteOpen ? fiche : null} testId="delete-collab" onClose={() => setDeleteOpen(false)}
        title={`Supprimer la fiche de ${fiche.display_name} ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une fiche déjà utilisée (compte, équipe, mission, pointage, dépense, document…) ne peut pas être supprimée : archivez-la à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/collaborators/${fiche.id}`, { data: { reason } });
            toast.success("Fiche mise à la corbeille");
            navigate("/collaborateurs");
          } catch (e) {
            setDeleteOpen(false);
            toast.error(errorMessage(e));
          }
        }}
      />
      <ArchiveDialog open={archiveOpen} onOpenChange={setArchiveOpen} fiche={fiche} onDone={load} onFailure={handleFailure} />
    </div>
  );
}

function AccountCard({ fiche, reload, onFailure, suggestions }) {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const account = fiche.account;

  useEffect(() => {
    if (account || fiche.archived) return;
    api.get("/collaborators/link-candidates").then(({ data }) => setCandidates(data)).catch(() => setCandidates({ users: [], suggestions: [] }));
  }, [account, fiche.archived, fiche.id]);

  const run = async (request, success) => {
    setBusy(true);
    try {
      await request();
      toast.success(success);
      setConfirmUnlink(false);
      await reload();
    } catch (error) {
      if (!onFailure(error)) toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const suggested = candidates?.suggestions?.filter((s) => s.collaborator_id === fiche.id).map((s) => s.user_id) || [];

  return (
    <Card className="p-5 space-y-3" data-testid="collab-account">
      <div className="font-display font-semibold text-sm text-slate-900">Compte CRM</div>
      {account ? (
        <>
          <div className="text-sm space-y-0.5">
            <div className="text-slate-900" data-testid="account-state">{ACCOUNT_STATE_LABELS[account.state] || account.state}</div>
            {account.name && <div className="text-slate-600">{account.name}</div>}
            {account.email && <div className="text-xs text-slate-500 break-all">{account.email}</div>}
          </div>
          <SuggestedRoleHint suggestions={suggestions} note="Le rôle du compte se change dans Utilisateurs." />
          <Button variant="outline" size="sm" onClick={() => setConfirmUnlink(true)} disabled={busy} data-testid="btn-account-unlink"><Unlink size={14} className="mr-1.5" /> Détacher le compte</Button>
          <Dialog open={confirmUnlink} onOpenChange={setConfirmUnlink}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Détacher le compte de {fiche.display_name} ?</DialogTitle>
                <DialogDescription>La fiche et son historique sont conservés. Le compte reste actif : il n'est ni supprimé ni désactivé.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmUnlink(false)}>Annuler</Button>
                <Button disabled={busy} data-testid="btn-account-unlink-confirm"
                  onClick={() => run(() => api.delete(`/collaborators/${fiche.id}/user-link`, { params: { expected_version: fiche.version } }), "Compte détaché")}>
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Détacher
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">{fiche.archived ? "Une fiche archivée ne peut pas être rattachée." : "Cette personne n'a pas de compte CRM. Rattachez-en un existant pour qu'elle retrouve sa fiche à la connexion."}</p>
          {!fiche.archived && (
            <>
              <Select value={choice} onValueChange={setChoice} disabled={candidates === null}>
                <SelectTrigger data-testid="select-account"><SelectValue placeholder={candidates === null ? "Chargement…" : "Choisir un compte"} /></SelectTrigger>
                <SelectContent>
                  {(candidates?.users || []).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}{suggested.includes(u.id) ? " (suggéré)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <SuggestedRoleHint suggestions={suggestions} note="Vous choisirez le rôle en créant le compte, ou dans Utilisateurs après le rattachement." />
              <Button variant="outline" size="sm" data-testid="btn-account-create"
                onClick={() => navigate("/users/nouveau", { state: { forCollaborator: { id: fiche.id, version: fiche.version, name: fiche.display_name, email: fiche.contacts?.email || "", whatsapp: fiche.contacts?.whatsapp || fiche.contacts?.phone || "", job_function_ids: fiche.job_function_ids || [] } } })}>
                <UserPlus size={14} className="mr-1.5" /> Créer un compte CRM pour cette personne
              </Button>
              <Button size="sm" disabled={!choice || busy} data-testid="btn-account-link"
                onClick={() => run(() => api.put(`/collaborators/${fiche.id}/user-link`, { expected_version: fiche.version, user_id: choice }), "Compte rattaché")}>
                {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Link2 size={14} className="mr-1.5" />} Rattacher
              </Button>
            </>
          )}
        </>
      )}
    </Card>
  );
}

// « Réaffecter l'équipe » : la même passation, pour une personne qui reste en poste. N'importe quel sous-ensemble : seuls les choix faits partent.
function ReassignDialog({ open, onOpenChange, fiche, onDone, onFailure }) {
  const handover = useHandoverImpact(open, fiche.id);
  const [choices, setChoices] = useState(emptyChoices());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setChoices(emptyChoices()); }, [open]);
  const impact = handover.state === "ready" ? handover.impact : null;
  const body = impact?.required ? buildHandover(impact, choices, { partial: true }) : null;
  const count = body ? body.reports.length + body.services.length : 0;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/collaborators/${fiche.id}/team-reassignment`, { expected_version: fiche.version, handover: body });
      toast.success(`${data.handover.reports} personne${data.handover.reports > 1 ? "s" : ""} et ${data.handover.services} service${data.handover.services > 1 ? "s" : ""} réaffecté${data.handover.reports + data.handover.services > 1 ? "s" : ""}`);
      onDone();
      onOpenChange(false);
    } catch (error) {
      if (isHandoverConflict(error)) {
        handover.reload();
        toast.warning("L'équipe a changé entre-temps : vérifiez la liste avant de réessayer.");
      } else if (!onFailure(error)) toast.error(errorMessage(error));
      else onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reassign-dialog" className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Réaffecter l'équipe</DialogTitle>
          <DialogDescription>Confiez des personnes ou des services de {fiche.display_name} à quelqu'un d'autre. Chaque changement est inscrit dans l'historique.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 sm:max-h-[60vh] sm:overflow-y-auto sm:pr-1">
          {handover.state === "loading" && <Skeleton className="h-24 w-full" />}
          {handover.state === "error" && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between gap-2" role="alert">
              <span>{handover.error}</span><Button size="sm" variant="outline" onClick={handover.reload}>Réessayer</Button>
            </div>
          )}
          {impact && !impact.required && (
            <div className="rounded-md border border-slate-200 p-4 text-sm text-slate-600" data-testid="reassign-empty">
              {fiche.display_name} n'a ni subordonné ni service à réaffecter pour le moment.
            </div>
          )}
          {impact?.required && <HandoverPanel impact={impact} choices={choices} onChange={setChoices} leaver={fiche.display_name} leaverId={fiche.id} mode="reassign" />}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!count || busy} data-testid="btn-reassign-confirm">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Réaffecter{count ? ` (${count})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ open, onOpenChange, fiche, initialTo, mandateNotice, onDone, onFailure }) {
  const options = allowedTransitions(fiche.status);
  const [to, setTo] = useState(options[0]?.to || "");
  const [values, setValues] = useState({ entry_date: "", exit_date: "", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const available = allowedTransitions(fiche.status);
      setTo(available.some((o) => o.to === initialTo) ? initialTo : available[0]?.to || "");
      setValues({ entry_date: "", exit_date: "", reason: "" });
    }
  }, [open, fiche.status, initialTo]);

  const spec = to ? statusFormSpec(fiche.status, to) : {};
  const leaving = to === "exited";
  const handover = useHandoverImpact(open && leaving, fiche.id);
  const [choices, setChoices] = useState(emptyChoices());
  useEffect(() => { if (open) setChoices(emptyChoices()); }, [open]);
  const impact = leaving && handover.state === "ready" ? handover.impact : null;
  const needsHandover = !!impact?.required;
  // la sortie n'est enregistrable qu'une fois la passation complète (le serveur le refuse de toute façon) et si l'appelant peut la terminer
  const handoverReady = !leaving || (handover.state === "ready" && (!needsHandover || (pendingCount(impact, choices) === 0 && !blockedReason(impact))));
  const valid = !!to && isStatusFormValid(fiche.status, to, values, today()) && handoverReady;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = buildStatusPayload(fiche.status, to, values, fiche.version);
      if (needsHandover) payload.handover = buildHandover(impact, choices);
      const { data } = await api.post(`/collaborators/${fiche.id}/status`, payload);
      toast.success(data.handover ? `Sortie enregistrée, ${data.handover.reports} personne${data.handover.reports > 1 ? "s" : ""} et ${data.handover.services} service${data.handover.services > 1 ? "s" : ""} réaffecté${data.handover.reports + data.handover.services > 1 ? "s" : ""}` : "Statut mis à jour");
      (data.warnings || []).forEach((w) => toast.warning(w.msg));
      onDone();
      onOpenChange(false);
    } catch (error) {
      const fresh = impactFromError(error);
      if (fresh) {
        // l'équipe a changé pendant la saisie : on affiche la situation à jour, sans perdre les choix encore valables
        handover.setImpact(fresh);
        setChoices((current) => pruneChoices(fresh, current));
        toast.warning("L'équipe de cette personne a changé : vérifiez la passation avant de confirmer.");
      } else if (leaving && isHandoverConflict(error) && error?.response?.data?.detail?.code === "handover_incomplete") {
        handover.reload();
        toast.warning("L'équipe de cette personne a changé : vérifiez la passation avant de confirmer.");
      } else if (!onFailure(error)) toast.error(errorMessage(error));
      else onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="status-dialog" className={leaving ? "sm:max-w-3xl" : undefined}>
        <DialogHeader>
          <DialogTitle>Changer le statut</DialogTitle>
          <DialogDescription>Statut actuel : {fiche.status === "pre_registered" ? "préinscrit" : fiche.status === "active" ? "actif" : fiche.status === "suspended" ? "suspendu" : "sorti"}. Les dates passées sont conservées dans le parcours.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 sm:max-h-[60vh] sm:overflow-y-auto sm:pr-1">
          <div className="space-y-2">
            <Label>Action</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="select-status-to"><SelectValue /></SelectTrigger>
              <SelectContent>{options.map((o) => <SelectItem key={o.to} value={o.to}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {spec.entryDate && (
            <div className="space-y-2"><Label htmlFor="s-entry">{spec.entryDateLabel} *</Label>
              <Input id="s-entry" type="date" max={today()} className="text-base md:text-sm" data-testid="input-status-entry" value={values.entry_date} onChange={(e) => setValues({ ...values, entry_date: e.target.value })} /></div>
          )}
          {spec.exitDate && (
            <div className="space-y-2"><Label htmlFor="s-exit">Date de sortie *</Label>
              <Input id="s-exit" type="date" max={today()} className="text-base md:text-sm" data-testid="input-status-exit" value={values.exit_date} onChange={(e) => setValues({ ...values, exit_date: e.target.value })} /></div>
          )}
          {leaving && handover.state === "loading" && <Skeleton className="h-24 w-full" data-testid="handover-loading" />}
          {leaving && handover.state === "error" && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between gap-2" role="alert" data-testid="handover-error">
              <span>Impossible de vérifier ce dont cette personne est responsable : {handover.error}</span>
              <Button size="sm" variant="outline" onClick={handover.reload}>Réessayer</Button>
            </div>
          )}
          {needsHandover && <HandoverPanel impact={impact} choices={choices} onChange={setChoices} leaver={fiche.display_name} leaverId={fiche.id} mode="exit" />}
          {to === "exited" && mandateNotice && !(needsHandover && impact?.mandate_in_force) && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="status-mandate-notice">{mandateNotice}</div>
          )}
          {spec.reason && (
            <div className="space-y-2"><Label htmlFor="s-reason">Motif *</Label>
              <Textarea id="s-reason" rows={2} maxLength={200} className="text-base md:text-sm" data-testid="input-status-reason" value={values.reason} onChange={(e) => setValues({ ...values, reason: e.target.value })} /></div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="btn-status-confirm">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Correction of the dates of the CURRENT period (administrator): the reason is mandatory, earlier periods are never touched.
function DatesDialog({ open, onOpenChange, fiche, onDone, onFailure }) {
  const [values, setValues] = useState({ entry_date: "", exit_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const exited = fiche.status === "exited";

  useEffect(() => {
    if (open) setValues({ entry_date: fiche.entry_date || "", exit_date: fiche.exit_date || "", reason: "" });
  }, [open, fiche.entry_date, fiche.exit_date]);

  const valid = isDatesCorrectionValid(fiche, values, today());
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/collaborators/${fiche.id}/dates-correction`, buildDatesCorrectionPayload(fiche, values));
      toast.success("Dates corrigées");
      onDone();
      onOpenChange(false);
    } catch (error) {
      if (!onFailure(error)) toast.error(errorInfo(error).data?.msg || errorMessage(error));
      else onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dates-dialog">
        <DialogHeader>
          <DialogTitle>Corriger les dates</DialogTitle>
          <DialogDescription>Période en cours de {fiche.display_name}. Les périodes passées ne changent pas ; l'ancienne et la nouvelle date sont conservées dans l'historique avec le motif.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label htmlFor="d-entry">Date d'entrée *</Label>
            <Input id="d-entry" type="date" max={today()} className="text-base md:text-sm" data-testid="input-dates-entry" value={values.entry_date} onChange={(e) => setValues({ ...values, entry_date: e.target.value })} /></div>
          {exited && (
            <div className="space-y-2"><Label htmlFor="d-exit">Date de sortie *</Label>
              <Input id="d-exit" type="date" max={today()} className="text-base md:text-sm" data-testid="input-dates-exit" value={values.exit_date} onChange={(e) => setValues({ ...values, exit_date: e.target.value })} /></div>
          )}
          <div className="space-y-2"><Label htmlFor="d-reason">Motif de la correction *</Label>
            <Textarea id="d-reason" rows={2} maxLength={200} className="text-base md:text-sm" data-testid="input-dates-reason" value={values.reason}
              placeholder="Ex. : erreur de saisie à la création de la fiche" onChange={(e) => setValues({ ...values, reason: e.target.value })} />
            <p className="text-xs text-slate-500">10 caractères au moins.</p></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="btn-dates-confirm">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Corriger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveDialog({ open, onOpenChange, fiche, onDone, onFailure }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const archiving = !fiche.archived;

  useEffect(() => {
    if (open) {
      setReason("");
      setBlocked(null);
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setBlocked(null);
    try {
      const path = archiving ? "archive" : "unarchive";
      const body = archiving ? { expected_version: fiche.version, reason: reason.trim() || null } : { expected_version: fiche.version };
      await api.post(`/collaborators/${fiche.id}/${path}`, body);
      toast.success(archiving ? "Fiche archivée" : "Fiche désarchivée");
      onDone();
      onOpenChange(false);
    } catch (error) {
      const info = errorInfo(error);
      if (info.status === 409 && info.data && info.code !== "version_conflict") setBlocked(info.data);
      else if (!onFailure(error)) toast.error(errorMessage(error));
      else onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="archive-dialog">
        <DialogHeader>
          <DialogTitle>{archiving ? `Archiver ${fiche.display_name} ?` : `Désarchiver ${fiche.display_name} ?`}</DialogTitle>
          <DialogDescription>
            {archiving
              ? "La fiche disparaît des listes et ne peut plus être modifiée. Son statut et tout son historique sont conservés ; vous pourrez la désarchiver."
              : "La fiche redevient visible et modifiable. Son statut n'est pas modifié."}
          </DialogDescription>
        </DialogHeader>
        {archiving && (
          <div className="space-y-2">
            <Label htmlFor="a-reason">Motif (facultatif)</Label>
            <Textarea id="a-reason" rows={2} maxLength={200} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="input-archive-reason" />
          </div>
        )}
        {blocked && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm space-y-1.5" role="alert" data-testid="archive-blocked">
            <div className="font-medium text-slate-900">{blocked.msg}</div>
            {(blocked.reports || []).length > 0 && <div>Responsable de : {blocked.reports.map((r) => r.display_name).join(", ")}</div>}
            {(blocked.services || []).length > 0 && <div>Chef ou remplaçant du service : {blocked.services.join(", ")}</div>}
            {blocked.others_hidden && <div className="text-xs text-slate-600">D'autres éléments hors de votre périmètre sont concernés : voyez un administrateur.</div>}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="btn-archive-confirm">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} {archiving ? "Archiver" : "Désarchiver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
